import React, { type ReactNode, useMemo, useRef } from 'react'

import { type GoogleMapActions } from '@/components/organisms/Map'
import { useTransportRequests } from '@/model/contexts/useTransportRequests.ts'
import { useAppMapBounds } from '@/pages/dashboard/map/useAppMapBounds'

type AppPageMapContextValue = GoogleMapActions & {
  focusTransportRequest(id?: string): void
  focusRegion(): void
  refresh(): void
}

export const AppPageMapContext = React.createContext<AppPageMapContextValue>({
  onMapLoad() {},
  onMapUnmount() {},
  focusTransportRequest() {},
  focusRegion() {},
  refresh() {},
})

export const AppPageMapProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const mapRef = useRef<google.maps.Map | undefined>(undefined)
  const userLatLngBoundsRef = useRef<google.maps.LatLngBounds | undefined>(undefined)
  const { computeRegionBounds, computeTransportRequestBounds } = useAppMapBounds()
  const { transportRequests } = useTransportRequests()

  const value = useMemo<AppPageMapContextValue>(
    () => ({
      onMapLoad: (map: google.maps.Map) => {
        mapRef.current = map
      },
      onMapUnmount: () => {
        mapRef.current = undefined
      },
      refresh: () => {
        if (!mapRef.current) return
        mapRef.current.fitBounds(computeRegionBounds())
      },
      focusTransportRequest: (id) => {
        if (!mapRef.current) return

        if (!id) {
          if (userLatLngBoundsRef.current) {
            mapRef.current.panTo(userLatLngBoundsRef.current.getCenter())
            userLatLngBoundsRef.current = undefined
          } else {
            mapRef.current.fitBounds(computeRegionBounds())
          }
          return
        }
        const request = transportRequests.find(({ _id }) => _id === id)
        if (!request) return

        if (!userLatLngBoundsRef.current) {
          userLatLngBoundsRef.current = mapRef.current.getBounds()
        }
        mapRef.current?.panTo(computeTransportRequestBounds(request).getCenter())
      },
      focusRegion: () => {
        if (!mapRef.current) return

        mapRef.current.fitBounds(computeRegionBounds())
      },
    }),
    [computeRegionBounds, computeTransportRequestBounds, transportRequests]
  )

  return <AppPageMapContext.Provider value={value}>{children}</AppPageMapContext.Provider>
}
